import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Paper,
  Grid,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Tip from 'components/Tip';
import {
  initializeFilters,
  initializeFiltersWithDefaultValue,
} from 'utils/commonTools';
import {
  translateKeyword,
  translation,
} from 'utils/translate';
import config from 'config';
import './PreFilters.css';
import texts from 'texts';
import { styled } from '@mui/material/styles';
import { setSearchFormFullScreen } from 'actions/searchFormFullScreenActions';
import queryString from 'query-string';

const StyledButton = styled(Button)({
  backgroundColor: 'var(--primary)',
  borderRadius: 5,
  border: 0,
  height: 48,
  textTransform: 'none', // Moved from label
  color: 'white', // Moved from label
  fontSize: '16px', // Moved from label
  '&:hover': {
    backgroundColor: 'var(--primary-dark)',
  },
});

const StyledInverseButton = styled(Button)({
  backgroundColor: 'white',
  borderRadius: 5,
  border: '3px solid var(--primary)',
  height: '48px', // Add unit
  textTransform: 'none', // Moved from label
  color: 'var(--primary)', // Moved from label
  fontSize: '16px', // Moved from label
  '&:hover': {
    backgroundColor: 'var(--grey-dark)',
  },
});

/* PREFILTERS COMPONENT */
const PreFilters = ({
  docGroup,
  handlePreFiltersSubmit,
  handlePreFiltersClear,
  isPrefilterOpen,
  handlePrefilterDockGroupChange,
}) => {
  const facetConstants = useSelector(
    (store) => store.facetConstants,
  );
  const lang = useSelector((store) => store.lang);

  const [selectedDocGroup, setSelectedDocGroup] =
    useState(docGroup);
  const [preFiltersInput, setPreFiltersInput] = useState();
  const [isOpen, setIsOpen] = useState(isPrefilterOpen);

  const [multiSelectFields, setMultiSelectFields] =
    useState(
      docGroup
        ? config.docGroups[docGroup].multiSelectFields
        : config.docGroup['all'].multiSelectFields,
    );
  const [radioSelectFields, setRadioSelectFields] =
    useState(
      docGroup
        ? config.docGroups[docGroup].radioSelectFields
        : config.docGroup['all'].radioSelectFields,
    );

  // initialize docGroup-specific constants
  const constants = useMemo(
    () => facetConstants[selectedDocGroup] || {},
    [selectedDocGroup, facetConstants],
  );
  const ymin = constants.min_year || 1970;
  const ymax = constants.max_year || 1970;
  // Generate array of years from ymin to ymax
  const years = [...Array(ymax - ymin + 1)].map(
    (_, i) => i + ymin,
  );

  const defaultPrefilters = useMemo(
    () => ({
      ...initializeFilters(multiSelectFields),
      ...initializeFiltersWithDefaultValue(
        radioSelectFields.map((field) => field.id),
        '',
      ),
      term: '',
      year_start: '',
      year_end: '',
      tunnus: '',
    }),
    [multiSelectFields, radioSelectFields],
  );

  // get and use possible prefilters from the URL, or use default prefilters
  useMemo(() => {
    // use defaults in case no prefilters in the URL
    if (!window.location.search.includes('prefilters')) {
      setPreFiltersInput(defaultPrefilters);
      return defaultPrefilters;
    }

    try {
      const searchQueries = queryString.parse(
        window.location.search,
      );
      const parsedPrefilters = JSON.parse(
        searchQueries.prefilters,
      );
      const newPrefilters = {
        ...defaultPrefilters,
        ...parsedPrefilters,
      };
      setPreFiltersInput(newPrefilters);
      return newPrefilters;
    } catch (error) {
      console.warn(error);
    }
  }, [defaultPrefilters]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handlePreFiltersChange = (event) => {
    setPreFiltersInput({
      ...preFiltersInput,
      ...{ [event.target.name]: event.target.value },
    });
  };

  const handleClear = () => {
    handlePreFiltersClear();
    setPreFiltersInput(defaultPrefilters);
  };

  // const removeChipFromPreFilters = (val, field) => {
  //   setPreFiltersInput({
  //     ...preFiltersInput,
  //     ...{
  //       [field]: preFiltersInput[field].filter(
  //         (item) => item !== val,
  //       ),
  //     },
  //   });
  // };
  const handleDocGroupSelectOnClick = async (event) => {
    docGroup = event.target.value;
    setSelectedDocGroup(event.target.value);
    setMultiSelectFields(
      config.docGroups[docGroup].multiSelectFields,
    );
    setRadioSelectFields(
      config.docGroups[docGroup].radioSelectFields,
    );
    //handlePrefilterDockGroupChange(event)
  };

  const handlePreFiltersSubmits = async (
    event,
    preFiltersInput,
  ) => {
    handlePreFiltersSubmit(
      event,
      preFiltersInput,
      selectedDocGroup,
    );
    setSearchFormFullScreen(true);
    setIsOpen(false);
  };

  return (
    <div className="prefilters-wrapper">
      {
        isOpen && (
          <div className="prefilters-form-container prefilters-form-container--open">
            <Paper
              elevation={0}
              className="prefilters-form"
            >
              <Grid container spacing={4}>
                {/* PREFILTER MULTISELECTS */}
                <Grid item xs={12} sm={8} md={6}>
                  <h4 className="doctype-label">
                    {translateKeyword('type', lang)}
                    <Tip
                      title={translation(
                        'asiakirjatyyppi_tooltip',
                        lang,
                      )}
                    >
                      <span
                        alt={translation(
                          'asiakirjatyyppi_tooltip',
                          lang,
                        )}
                        className="tooltip-help tippable"
                      ></span>
                    </Tip>
                  </h4>
                  <Select
                    sx={{
                      borderLeft:
                        '2px solid var(--gray-dark)',
                      borderBottom:
                        '2px solid var(--gray-dark)',
                      borderRadius: '3px 0px 0px 3px',
                      padding: '10 14px',
                      width: '70%',
                      backgroundColor: 'var(--light-gray)',
                      display: 'inline-block',
                      lineHeight: '1.1876em',
                    }}
                    value={selectedDocGroup}
                    name="docGroup"
                    onChange={handleDocGroupSelectOnClick}
                  >
                    {Object.entries(config.docGroups).map(
                      ([key, value]) =>
                        value.searchable && (
                          <MenuItem value={key} key={key}>
                            {translation(value.name, lang)}
                          </MenuItem>
                        ),
                    )}
                  </Select>
                  {selectedDocGroup !== 'all' && (
                    <div className="docgroup-info-div">
                      <div>
                        {
                          texts.contentDescription[
                            selectedDocGroup
                          ].text[lang]
                        }
                      </div>
                      <div className="docgroup-info-source">
                        {
                          texts.contentDescription[
                            selectedDocGroup
                          ].source[lang]
                        }
                      </div>
                    </div>
                  )}
                  {preFiltersInput && (
                    <>
                      <div className="multiselect-div">
                        {multiSelectFields.map((field) => (
                          <>
                            <h4 className="search-term-title">
                              {translateKeyword(
                                field,
                                lang,
                              )}
                            </h4>
                            <FormControl
                              variant="outlined"
                              className="prefilters__form-control"
                              key={field}
                            >
                              <InputLabel
                                labelid={
                                  'preFilter-' + field
                                }
                              >
                                {translation(
                                  'valitse',
                                  lang,
                                ) +
                                  translateKeyword(
                                    field,
                                    lang,
                                  ).toLowerCase()}
                              </InputLabel>
                              <Select
                                multiple
                                labelid={
                                  'preFilter-' + field
                                }
                                label={
                                  translation(
                                    'valitse',
                                    lang,
                                  ) +
                                  translateKeyword(
                                    field,
                                    lang,
                                  ).toLowerCase()
                                }
                                value={
                                  preFiltersInput[field]
                                }
                                name={field}
                                onChange={
                                  handlePreFiltersChange
                                }
                              >
                                {Object.keys(
                                  constants[field],
                                ).map((val) => (
                                  <MenuItem
                                    key={val}
                                    value={val}
                                  >
                                    {/* {val} */}
                                    {val !== ''
                                      ? translation(
                                          val,
                                          lang,
                                        )
                                        ? translation(
                                            val,
                                            lang,
                                          )
                                        : val
                                      : null}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        ))}
                      </div>
                      {/* PREFILTER RADIOBUTTON */}
                      {radioSelectFields.map(
                        (radioField) => (
                          <div>
                            <FormControl
                              className="prefilters__form-control"
                              key={radioField.id}
                            >
                              <h4 className="search-term-title">
                                {translation(
                                  'prefilter',
                                  radioField.name,
                                  'name',
                                  lang,
                                )}
                                <Tip
                                  title={translation(
                                    'prefilter',
                                    radioField.name,
                                    'tooltip',
                                    lang,
                                  )}
                                >
                                  <span
                                    alt={texts.info.help}
                                    className="tooltip-help tippable"
                                  ></span>
                                </Tip>
                              </h4>

                              <RadioGroup
                                aria-label={radioField.name}
                                value={
                                  preFiltersInput[
                                    radioField.id
                                  ]
                                }
                                name={radioField.id}
                                onChange={
                                  handlePreFiltersChange
                                }
                              >
                                <FormControlLabel
                                  value=""
                                  control={<Radio />}
                                  label={translation(
                                    'prefilter',
                                    'no_filters',
                                    lang,
                                  )}
                                  key="Ei rajausta"
                                />
                                {radioField.fields.map(
                                  (field) => (
                                    <FormControlLabel
                                      value={field}
                                      control={<Radio />}
                                      label={translation(
                                        'prefilter',
                                        radioField.name,
                                        field,
                                        lang,
                                      )}
                                      key={
                                        field +
                                        radioField.name
                                      }
                                    />
                                  ),
                                )}
                              </RadioGroup>
                            </FormControl>
                          </div>
                        ),
                      )}

                      {/* <Grid item xs={12} sm={10} md={8}> */}
                      {/* PREFILTER START YEAR */}
                      <div className="years-div">
                        <div className="start-year-div">
                          <h4 className="year-title">
                            {translation(
                              'prefilter',
                              'from_year',
                              lang,
                            )}
                          </h4>
                          <FormControl
                            className="prefilters__form-control--50"
                            variant="outlined"
                          >
                            <InputLabel
                              // htmlFor="year_start"
                              // shrink={!!preFiltersInput?.year_start}
                              id="valitse-vuosi-start"
                            >
                              {translation(
                                'valitse_vuosi',
                                lang,
                              )}
                            </InputLabel>
                            <Select
                              labelid="valitse-vuosi-start"
                              label="Valitse vuosi"
                              value={
                                preFiltersInput?.year_start
                              }
                              // className="prefilters-form__input prefilters-form__rangeselect"
                              onChange={
                                handlePreFiltersChange
                              }
                              inputProps={{
                                name: 'year_start',
                                id: 'year_start',
                              }}
                            >
                              <MenuItem value="">
                                <i>
                                  {translation(
                                    'prefilter',
                                    'not_selected',
                                    lang,
                                  )}
                                </i>
                              </MenuItem>
                              {years
                                .filter(
                                  (val) =>
                                    val <=
                                      preFiltersInput?.year_end ||
                                    !preFiltersInput?.year_end,
                                )
                                .map((val) => (
                                  <MenuItem
                                    key={val}
                                    value={val}
                                  >
                                    {val}
                                  </MenuItem>
                                ))}
                              {years
                                .filter(
                                  (val) =>
                                    preFiltersInput?.year_end &&
                                    val >
                                      preFiltersInput?.year_end,
                                )
                                .map((val) => (
                                  <MenuItem
                                    key={val}
                                    value={val}
                                    disabled
                                  >
                                    {val}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </div>

                        {/* PREFILTER END YEAR */}
                        <div className="start-year-div">
                          <h4 className="year-title">
                            {translation(
                              'prefilter',
                              'to_year',
                              lang,
                            )}
                          </h4>
                          <FormControl
                            className="prefilters__form-control--50"
                            variant="outlined"
                          >
                            <InputLabel
                              // htmlFor="year_end"
                              // shrink={!!preFiltersInput?.year_end}
                              id="valitse-vuosi-end"
                            >
                              {translation(
                                'valitse_vuosi',
                                lang,
                              )}
                            </InputLabel>
                            <Select
                              labelid="valitse-vuosi-end"
                              label="Valitse vuosi"
                              value={
                                preFiltersInput?.year_end
                              }
                              className="prefilters-form__input prefilters-form__rangeselect"
                              onChange={
                                handlePreFiltersChange
                              }
                              inputProps={{
                                name: 'year_end',
                                id: 'year_end',
                              }}
                            >
                              <MenuItem value="">
                                <i>
                                  {translation(
                                    'prefilter',
                                    'not_selected',
                                    lang,
                                  )}
                                </i>
                              </MenuItem>
                              {years
                                .filter(
                                  (val) =>
                                    preFiltersInput?.year_start &&
                                    val <
                                      preFiltersInput?.year_start,
                                )
                                .map((val) => (
                                  <MenuItem
                                    key={val}
                                    value={val}
                                    disabled
                                  >
                                    {val}
                                  </MenuItem>
                                ))}
                              {years
                                .filter(
                                  (val) =>
                                    val >=
                                      preFiltersInput?.year_start ||
                                    !preFiltersInput?.year_start,
                                )
                                .map((val) => (
                                  <MenuItem
                                    key={val}
                                    value={val}
                                  >
                                    {val}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>

                      {/* PREFILTER SEARCH TERM */}
                      <h4 className="search-term-title">
                        {translation(
                          'prefilter',
                          'search_term',
                          lang,
                        )}
                      </h4>
                      <FormControl
                        className="prefilters__form-control"
                        variant="outlined"
                      >
                        <TextField
                          id="prefilters-term"
                          label={translation(
                            'prefilter',
                            'search_term',
                            lang,
                          )}
                          className="prefilters-form__input prefilters-form__searchterm"
                          name="term"
                          value={preFiltersInput?.term}
                          onChange={handlePreFiltersChange}
                          variant="outlined"
                        />
                      </FormControl>

                      {/* PREFILTER-TUNNUS */}
                      <h4 className="search-term-title">
                        {translation(
                          'prefilter',
                          'diaarinumerot',
                          lang,
                        )}
                      </h4>
                      <FormControl className="prefilters__form-control">
                        <TextField
                          id="prefilters-tunnus"
                          label={translation(
                            'prefilter',
                            'diaarinumerot',
                            lang,
                          )}
                          name="tunnus"
                          multiline
                          maxrows="5"
                          className="prefilters-form__input prefilters-form__tunnus"
                          value={preFiltersInput?.tunnus}
                          onChange={handlePreFiltersChange}
                          variant="outlined"
                        />
                      </FormControl>

                      {/* PREFILTER SUBMIT/RESET */}
                      <div className="buttons-div">
                        <StyledButton
                          variant="contained"
                          className="prefilters-button"
                          onClick={(e) =>
                            handlePreFiltersSubmits(
                              e,
                              preFiltersInput,
                            )
                          }
                        >
                          {translation(
                            'prefilter',
                            'name',
                            lang,
                          )}
                        </StyledButton>
                        <StyledInverseButton
                          className="reset-prefilters"
                          onClick={handleClear}
                        >
                          {translation(
                            'prefilter',
                            'remove',
                            lang,
                          )}
                        </StyledInverseButton>
                      </div>
                    </>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </div>
        )
        // )
      }
      {/* {!isOpen &&
      <>
        <SearchForm docGroup={selectedDocGroup} />
        <div className="prefilters-button-container">
          <Tip title={translation("prefilter", "tooltip", lang)}>
            <LinkLookButton
              id="prefilters-toggler"
              className="prefilters-toggler"
              disableTouchRipple
              onClick={() => toggleOpen()}
            >
              <EditIcon className="button-icon" />
              {translation("prefilter", "button", lang)}
            </LinkLookButton>
          </Tip>
        </div>
        </>
      } */}
    </div>
  );
};

PreFilters.propTypes = {
  docGroup: PropTypes.string.isRequired,
  handlePreFiltersSubmit: PropTypes.func.isRequired,
  handlePreFiltersClear: PropTypes.func.isRequired,
};

export default PreFilters;
